import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'

/**
 * Bottom-docked Inspector panel state.
 *
 * Tracks open/closed + height. The "what's inspected" content is
 * derived elsewhere — for now the Inspector reads `focusedPad` +
 * active bus / project state directly from the sequencer store.
 * A future `target` field here can explicitly pin a specific
 * pad/bus regardless of sequencer focus.
 *
 * Height is persisted so the user's drag-to-resize choice survives
 * reloads.
 */

export type InspectorTargetKind = 'pad' | 'bus' | 'project'

interface InspectorState {
  isOpen: boolean
  /** Height in pixels when the panel is open. Drag-resized by the
   *  user. Falls back to 280 on first visit. */
  height: number
  /** Explicit override — when non-null, the Inspector renders this
   *  target regardless of sequencer focus. Cleared on close. */
  pinned: { kind: InspectorTargetKind; id: string | number } | null

  setOpen: (open: boolean) => void
  toggle: () => void
  setHeight: (height: number) => void
  pinTarget: (target: {
    kind: InspectorTargetKind
    id: string | number
  }) => void
  clearPin: () => void
}

const MIN_HEIGHT = 180
const MAX_HEIGHT = 640

export const useInspectorStore = create<InspectorState>()(
  persist(
    set => ({
      isOpen: false,
      height: 280,
      pinned: null,

      setOpen: open => set({ isOpen: open }),
      toggle: () => set(state => ({ isOpen: !state.isOpen })),
      setHeight: h =>
        set({ height: Math.max(MIN_HEIGHT, Math.min(MAX_HEIGHT, h)) }),
      pinTarget: target => set({ pinned: target, isOpen: true }),
      clearPin: () => set({ pinned: null }),
    }),
    {
      name: 'busswerks-inspector',
      partialize: state => ({ isOpen: state.isOpen, height: state.height }),
      storage: createJSONStorage(() =>
        typeof window === 'undefined'
          ? {
              getItem: () => null,
              setItem: () => {},
              removeItem: () => {},
            }
          : window.localStorage,
      ),
    },
  ),
)
